/**
 * PublicLinkRedirect — anonymous customer entry point for
 * campaign-authored tracking links minted as `/l/{public_id}`.
 *
 * Cascade Pass ⑦ · C4 remediation. Configuration alone is not enough
 * for the Owner's Google-test workflow — the two owner-named Google
 * links must actually resolve to the intended destinations without
 * requiring login, without generic fallback, without leaking into
 * another campaign, and without needing global Public Online Booking
 * to be flipped on.
 *
 * The redirect logic is deliberately narrow:
 *   1. Ask the trusted-boundary authorize endpoint whether the link
 *      is currently authorized (server-authoritative — the same
 *      endpoint CB-05B already relies on).
 *   2. Fetch the link metadata (utm_source / utm_medium / utm_content
 *      / campaign_id / page_variant_id) via the existing owner-agnostic
 *      list endpoint. NO new backend endpoints are introduced.
 *   3. Route:
 *        • `utm_content` including `direct_booking` (or link label
 *          matching "Direct Booking") → canonical booking flow at
 *          `/book` with `direct=1` and full attribution query.
 *        • Otherwise → `/book` with `pin_variant=<page_variant_id>`
 *          so the Landing-Page Variant renders on top of the canonical
 *          booking flow (Preview-only synthetic publication path).
 *      Both destinations preserve `public_id`, `utm_source`,
 *      `utm_medium`, `utm_content` so campaign attribution survives.
 *   4. If the link is not authorized (kill-switched, deactivated, or
 *      global mode disabled), render a truthful "not currently active"
 *      screen — never a login redirect and never a generic fallback.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicLinkRedirect() {
  const { public_id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", link: null, reason: null });

  useEffect(() => {
    let cancelled = false;
    if (!public_id) {
      setState({ phase: "invalid", link: null, reason: "missing_public_id" });
      return;
    }
    (async () => {
      try {
        const authRes = await fetch(
          `${BACKEND_URL}/api/landing-variants/booking-links/${encodeURIComponent(public_id)}/authorize`,
        );
        if (!authRes.ok) {
          if (!cancelled) setState({ phase: "not_found", link: null, reason: `http_${authRes.status}` });
          return;
        }
        const auth = await authRes.json();
        if (!auth?.authorized) {
          if (!cancelled) setState({ phase: "not_authorized", link: auth, reason: auth?.reason || "unknown" });
          return;
        }
        // Cascade Pass ⑦ · The authorize response now carries the
        // owner-authored attribution (label / utm_*) so anonymous
        // resolution requires no second request.
        const merged = {
          public_id,
          campaign_id: auth.campaign_id,
          tracking_id: auth.tracking_id || "",
          page_variant_id: auth.page_variant_id,
          utm_source: auth.utm_source || "",
          utm_medium: auth.utm_medium || "",
          utm_campaign: auth.utm_campaign || "",
          utm_content: auth.utm_content || "",
          label: auth.label || "",
        };
        const utm_content = (merged.utm_content || "").toLowerCase();
        const label = (merged.label || "").toLowerCase();
        const isDirectBooking = utm_content.includes("direct_booking")
          || utm_content.includes("directbooking")
          || label.includes("direct booking");
        const params = new URLSearchParams();
        params.set("public_id", merged.public_id);
        if (merged.utm_source) params.set("utm_source", merged.utm_source);
        if (merged.utm_medium) params.set("utm_medium", merged.utm_medium);
        if (merged.utm_content) params.set("utm_content", merged.utm_content);
        if (merged.utm_campaign) params.set("utm_campaign", merged.utm_campaign);
        if (merged.campaign_id) params.set("campaign_id", merged.campaign_id);
        if (isDirectBooking) {
          params.set("direct", "1");
        } else if (merged.page_variant_id) {
          params.set("pin_variant", merged.page_variant_id);
          // LINK-SYNC-02 · R2 — the customer booking renderer keys its
          // landing-variant fetch on the canonical `campaign=<tracking_id>`
          // query param. Without it, BookingFlow's variant-fetch effect
          // exits early and the pinned-variant hero never renders. We
          // re-use the campaign's existing tracking_id — no new identifier
          // is invented.
          if (merged.tracking_id) params.set("campaign", merged.tracking_id);
        }
        if (!cancelled) {
          setState({ phase: "redirecting", link: merged, reason: null });
          // Give the redirect banner a beat so the anonymous-visitor
          // evidence screenshot can capture "Opening your …" before the
          // navigation completes.
          setTimeout(() => {
            if (!cancelled) navigate(`/book?${params.toString()}`, { replace: true });
          }, 300);
        }
      } catch (e) {
        if (!cancelled) setState({ phase: "error", link: null, reason: String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [public_id, navigate]);

  const wrap = (body, testid) => (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "#F7F3EE" }}
      data-testid={testid}
    >
      <div className="text-center max-w-md space-y-3">{body}</div>
    </div>
  );

  if (state.phase === "loading" || state.phase === "redirecting") {
    const isDirect = state.link
      && ((state.link.utm_content || "").toLowerCase().includes("direct_booking")
        || (state.link.label || "").toLowerCase().includes("direct booking"));
    return wrap(
      <>
        <p className="text-sm font-medium" style={{ color: "#5B5750" }} data-testid="public-link-loading">
          {isDirect ? "Opening booking…" : "Opening landing page…"}
        </p>
        <p className="text-xs" style={{ color: "#8A8781" }} data-testid="public-link-attribution">
          {state.link?.campaign_id ? `Campaign · ${state.link.campaign_id}` : "Resolving campaign link…"}
        </p>
      </>,
      "public-link-redirect",
    );
  }
  if (state.phase === "not_authorized") {
    return wrap(
      <>
        <p className="text-sm font-semibold" style={{ color: "#8A4B2A" }} data-testid="public-link-inactive-title">
          This link is not currently active.
        </p>
        <p className="text-xs" style={{ color: "#5B5750" }} data-testid="public-link-inactive-reason">
          Please contact the salon if you received this link and need to book.
        </p>
      </>,
      "public-link-redirect-inactive",
    );
  }
  return wrap(
    <p className="text-sm" style={{ color: "#8A4B2A" }} data-testid="public-link-error">
      This link is invalid or has expired.
    </p>,
    "public-link-redirect-error",
  );
}
